/**
 * Local cache for the /songs list, so the page renders instantly on repeat
 * visits instead of waiting for the network round-trip. RTK Query still
 * runs in the background and refreshes the cache; the user only sees a
 * loading state on their very first visit ever.
 *
 * Why localStorage and not IndexedDB: song metadata is small (title, ids,
 * dates, thumbnail URL — ~1KB × 100 jobs = ~100KB), well within
 * localStorage's ~5MB budget. Synchronous reads mean no useEffect flicker
 * on mount. Audio blob caching is a separate concern (task #372) and
 * would use IndexedDB when we get there.
 *
 * Keyed by userId so that logging out + in as a different user shows the
 * right cache. Missing userId → no cache read/write (anon fallthrough).
 *
 * Schema is versioned so a shape change on the backend can invalidate
 * old caches by bumping VERSION rather than forcing a hard clear.
 */

const VERSION = 1;
const NS = 'mixgenie:cache:v' + VERSION;

type CacheEnvelope<T> = {
  v: number;
  syncedAt: number;
  data: T;
};

function safeReadJSON<T>(key: string): CacheEnvelope<T> | null {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CacheEnvelope<T>;
    if (parsed?.v !== VERSION) return null;
    return parsed;
  } catch {
    return null;
  }
}

function safeWriteJSON<T>(key: string, data: T): number {
  const now = Date.now();
  try {
    const env: CacheEnvelope<T> = { v: VERSION, syncedAt: now, data };
    localStorage.setItem(key, JSON.stringify(env));
  } catch {
    // Quota exceeded or private mode — best-effort, silently drop.
  }
  return now;
}

function keyFor(kind: string, userKey: string): string {
  return `${NS}:${kind}:${userKey}`;
}

// ---------------------------------------------------------------------------
// Songs (jobs)
// ---------------------------------------------------------------------------

export function readCachedSongs(userKey: string): { data: any[]; syncedAt: number } | null {
  if (!userKey) return null;
  const env = safeReadJSON<any[]>(keyFor('songs', userKey));
  if (!env || !Array.isArray(env.data)) return null;
  return { data: env.data, syncedAt: env.syncedAt };
}

export function writeCachedSongs(userKey: string, songs: any[]): number {
  if (!userKey || !Array.isArray(songs)) return 0;
  return safeWriteJSON<any[]>(keyFor('songs', userKey), songs);
}

// ---------------------------------------------------------------------------
// Collections (playlists)
// ---------------------------------------------------------------------------

export function readCachedCollections(userKey: string): { data: any[]; syncedAt: number } | null {
  if (!userKey) return null;
  const env = safeReadJSON<any[]>(keyFor('collections', userKey));
  if (!env || !Array.isArray(env.data)) return null;
  return { data: env.data, syncedAt: env.syncedAt };
}

export function writeCachedCollections(userKey: string, collections: any[]): number {
  if (!userKey || !Array.isArray(collections)) return 0;
  return safeWriteJSON<any[]>(keyFor('collections', userKey), collections);
}

// ---------------------------------------------------------------------------
// Clear on logout — user-scoped only. Doesn't touch other users' caches
// (there aren't any on this device, but keeps the surface clean).
// ---------------------------------------------------------------------------

export function clearCachedSongsFor(userKey: string): void {
  if (!userKey) return;
  try {
    localStorage.removeItem(keyFor('songs', userKey));
    localStorage.removeItem(keyFor('collections', userKey));
  } catch {}
}
